import { basicEventSchema } from "@/pages/events/schemas/event.create.form";
import type { HitlReason, PipelineItem } from "@/types/tina";

import {
  readMatchedArtists,
  readMatchedVenue,
  readMusicEventContext,
} from "./hitl-readers";
import type { MusicEventContext, TextRef } from "./hitl-readers";

const eventTypeOptions = basicEventSchema.shape.type.options;
type EventTypeOption = (typeof eventTypeOptions)[number];

/**
 * Tina-HITL prefill refs may carry only an `id` when a matched Mood entity
 * is known but no display name is available on the payload. Consumers must
 * handle the id-only case (resolve via repository before persisting).
 */
export interface TinaHitlEventPrefillEntityRef {
  id?: string;
  name?: string;
}

export interface TinaHitlEventPrefillPayload {
  source: "tina-hitl";
  name?: string;
  about?: string;
  startDate?: string;
  endDate?: string;
  type?: EventTypeOption;
  genres?: TinaHitlEventPrefillEntityRef[];
  venue?: TinaHitlEventPrefillEntityRef;
  lineup?: TinaHitlEventPrefillEntityRef[];
  timezone?: string;
  city?: string;
  ticketingUrl?: string;
  matchedEntityIds: {
    venueId?: string;
    artistIds: string[];
  };
  autoCloseHitl: true;
  metadata: {
    tinaItemId: string;
    tinaRunId: string;
    hitlReason: HitlReason;
    contentType: "music_event";
    url?: string;
    site?: string;
  };
  warnings: string[];
}

export function buildTinaHitlEventPrefill(
  item: PipelineItem,
): TinaHitlEventPrefillPayload | null {
  const ctx = item.hitl_context;
  if (!ctx || ctx.content_type !== "music_event") return null;

  const musicContext = readMusicEventContext(item, ctx);
  if (!musicContext) return null;

  const matchedVenue = readMatchedVenue(item, ctx);
  const matchedArtists = readMatchedArtists(item, ctx);
  const venue = buildVenueRef(matchedVenue, musicContext);
  const lineup = buildLineupRefs(matchedArtists, musicContext.lineupNames);
  const warnings = buildWarnings(musicContext, venue, lineup);

  return {
    source: "tina-hitl",
    name: musicContext.name,
    about: musicContext.about,
    startDate: musicContext.startDate,
    endDate: musicContext.endDate,
    type: mapEventType(musicContext.type),
    genres: musicContext.genreNames.map((name) => ({ name })),
    venue,
    lineup,
    timezone: musicContext.timezone,
    city: musicContext.cityName,
    ticketingUrl: musicContext.ticketingUrl,
    matchedEntityIds: {
      venueId: venue?.id,
      artistIds: lineup.flatMap((artist) => (artist.id ? [artist.id] : [])),
    },
    autoCloseHitl: true,
    metadata: {
      tinaItemId: item.id,
      tinaRunId: item.run_id,
      hitlReason: item.hitl_reason ?? ctx.reason,
      contentType: "music_event",
      url: musicContext.sourceUrl,
      site: musicContext.site,
    },
    warnings,
  };
}

function buildVenueRef(
  matchedVenue: TextRef | null,
  musicContext: MusicEventContext,
): TinaHitlEventPrefillEntityRef | undefined {
  const id = matchedVenue?.id;
  const name = matchedVenue?.name ?? musicContext.venueName;
  if (!id && !name) return undefined;
  return name ? { id, name } : { id };
}

function buildLineupRefs(
  matchedArtists: TextRef[],
  lineupNames: string[],
): TinaHitlEventPrefillEntityRef[] {
  const refs: TinaHitlEventPrefillEntityRef[] = [];
  const seen = new Set<string>();

  for (const artist of matchedArtists) {
    const ref: TinaHitlEventPrefillEntityRef = artist.name
      ? { id: artist.id, name: artist.name }
      : { id: artist.id };
    if (rememberLineupRef(ref, seen)) refs.push(ref);
  }

  for (const name of lineupNames) {
    const ref = { name };
    if (rememberLineupRef(ref, seen)) refs.push(ref);
  }

  return refs;
}

function rememberLineupRef(
  ref: TinaHitlEventPrefillEntityRef,
  seen: Set<string>,
): boolean {
  const keys = [
    ref.id ? `id:${ref.id}` : null,
    ref.name ? `name:${ref.name.toLowerCase()}` : null,
  ].filter((key): key is string => Boolean(key));
  if (keys.length === 0) return false;
  if (keys.some((key) => seen.has(key))) return false;
  keys.forEach((key) => seen.add(key));
  return true;
}

function buildWarnings(
  musicContext: MusicEventContext,
  venue: TinaHitlEventPrefillEntityRef | undefined,
  lineup: TinaHitlEventPrefillEntityRef[],
): string[] {
  const warnings: string[] = [];

  if (!musicContext.name) warnings.push("Missing event name.");
  if (!musicContext.startDate) warnings.push("Missing event start date.");
  if (!musicContext.timezone) warnings.push("Missing event timezone.");
  if (!venue) {
    warnings.push("Missing venue.");
  } else if (!venue.id && venue.name) {
    warnings.push(`Venue without matched Mood id: ${quoteValue(venue.name)}.`);
  }
  if (lineup.length === 0) {
    warnings.push("Missing lineup.");
  } else {
    const unmatchedArtists = lineup.filter((artist) => !artist.id);
    if (unmatchedArtists.length > 0) {
      warnings.push(
        `Lineup artists without matched Mood ids: ${formatQuotedList(
          unmatchedArtists.map((artist) => artist.name ?? "Unknown artist"),
        )}.`,
      );
    }
  }

  return warnings;
}

function formatQuotedList(values: string[]): string {
  const quoted = values.map(quoteValue);
  if (quoted.length <= 2) return quoted.join(" and ");
  return `${quoted.slice(0, -1).join(", ")}, and ${quoted.at(-1)}`;
}

function quoteValue(value: string): string {
  return `"${value}"`;
}

const eventTypeAliases: Record<string, EventTypeOption> = {
  djset: "DJ set",
};

const eventTypeByNormalized: Map<string, EventTypeOption> = new Map([
  ...eventTypeOptions.map((option): [string, EventTypeOption] => [
    option.toLowerCase(),
    option,
  ]),
  ...Object.entries(eventTypeAliases),
]);

function mapEventType(value: string | undefined): EventTypeOption {
  const normalized = value?.trim().toLowerCase();
  if (!normalized) return "Event";
  return eventTypeByNormalized.get(normalized) ?? "Event";
}
